/**
 * InvoiceFlow : entrée par facture / bon de livraison. L'IA lit le document ;
 * chaque ligne est rapprochée (Existant / Nouvelle réf.), éditable, puis validée en groupe.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { scannerApi } from '../../services/api';
import { useUndoToast } from '../../hooks/useUndoToast';
import { Button, Spinner } from '../../components/ui/kit';
import { ReferencePicker } from '../../components/wine/ReferencePicker';
import { formatEuro } from '../../utils/format';

/** Extrait un millésime (4 chiffres) d'un libellé brut. */
function parseYear(label) {
  const m = (label || '').match(/(19|20)\d{2}/);
  return m ? m[0] : 'NV';
}

export default function InvoiceFlow({ initialImage, preview, onCancel }) {
  const qc = useQueryClient();
  const { notify } = useUndoToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [opts, setOpts] = useState({ update_prices: true, range_locations: true });

  useEffect(() => {
    scannerApi.analyzeInvoice(initialImage).then((res) => {
      setData(res);
      setLines(res.lines.map((l) => ({
        libelle_brut: l.libelle_brut, quantite: l.quantite || 0, pu_ht: l.pu_ht || 0,
        year: parseYear(l.libelle_brut), format: '750',
        cuvee_id: l.status === 'existant' ? l.match?.cuvee_id : null,
        matched_name: l.status === 'existant' ? l.match?.name : null,
        status: l.status, color: l.match?.color || 'rouge',
      })));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [initialImage]);

  const upd = (i, patch) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const validate = async () => {
    const payload = {
      document: data.invoice,
      update_prices: opts.update_prices,
      range_locations: opts.range_locations,
      lines: lines.map((l) => l.cuvee_id
        ? { cuvee_id: l.cuvee_id, year: l.year, format: l.format, quantity: l.quantite, pu_ht: l.pu_ht }
        : { new_cuvee: { name: l.libelle_brut }, year: l.year, format: l.format, quantity: l.quantite, pu_ht: l.pu_ht }),
    };
    const res = await scannerApi.invoice(payload);
    qc.invalidateQueries({ queryKey: ['stock'] });
    qc.invalidateQueries({ queryKey: ['alerts'] });
    qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
    notify(`Facture validée · ${res.total_bottles} btl`, () => scannerApi.undo(res.group_id).then(() => qc.invalidateQueries({ queryKey: ['stock'] })));
    onCancel();
  };

  if (loading) return <Spinner label="Lecture de la facture…" />;
  if (!data) return <p className="text-n600">Lecture impossible.</p>;

  const totalBottles = lines.reduce((s, l) => s + Number(l.quantite || 0), 0);
  const totalHt = lines.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.pu_ht || 0), 0);

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6" data-testid="invoice-flow">
      {/* Document */}
      <div>
        <div className="vd-plate w-full"><img src={preview} alt="Facture" className="w-full rounded" /></div>
        <div className="mt-3 text-[13px] space-y-1">
          <p><span className="text-n500">Fournisseur : </span>{data.invoice.fournisseur || '—'}</p>
          <p><span className="text-n500">N° : </span>{data.invoice.numero || '—'}</p>
          <p><span className="text-n500">Total HT lu : </span><span className="nums">{formatEuro(data.invoice.total_ht)}</span></p>
          {data.ecart !== 0 && <p className="text-gold700">Écart lignes / total : {formatEuro(data.ecart)}</p>}
        </div>
      </div>

      {/* Lignes */}
      <div>
        <table className="w-full text-[13px]" data-testid="invoice-lines">
          <thead><tr className="kicker text-left">
            <th className="py-2">Ligne</th><th className="py-2">Rappr.</th><th className="py-2">Mill.</th>
            <th className="py-2 text-right">Qté</th><th className="py-2 text-right">PU HT</th><th className="py-2 text-right">Total</th>
          </tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="hair-t align-top" data-testid={`invoice-line-${i}`}>
                <td className="py-2 pr-2 max-w-[200px]">
                  <span className="block">{l.matched_name || l.libelle_brut}</span>
                  <span className="block text-[11px] text-n500 italic">« {l.libelle_brut} »</span>
                  {!l.cuvee_id && (
                    <div className="mt-1"><ReferencePicker testId={`line-picker-${i}`} onSelect={(r) => upd(i, { cuvee_id: r.cuvee_id, matched_name: r.name, year: r.year, format: r.format === undefined ? l.format : l.format, status: 'existant', color: r.color })} /></div>
                  )}
                </td>
                <td className="py-2"><span className={`vd-tag ${l.status === 'existant' ? 'vd-tag-fill' : 'vd-tag-accent'}`}>{l.status === 'existant' ? 'Existant' : 'Nouvelle réf.'}</span></td>
                <td className="py-2"><input value={l.year} onChange={(e) => upd(i, { year: e.target.value })} className="vd-input h-8 w-16" data-testid={`line-year-${i}`} /></td>
                <td className="py-2 text-right"><input value={l.quantite} onChange={(e) => upd(i, { quantite: Number(e.target.value) })} className="vd-input h-8 w-16 text-right nums" data-testid={`line-qty-${i}`} /></td>
                <td className="py-2 text-right"><input value={l.pu_ht} onChange={(e) => upd(i, { pu_ht: Number(e.target.value) })} className="vd-input h-8 w-20 text-right nums" data-testid={`line-pu-${i}`} /></td>
                <td className="py-2 text-right nums">{formatEuro(Number(l.quantite || 0) * Number(l.pu_ht || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="hair-t"><td className="py-2 kicker" colSpan={3}>Total</td>
            <td className="py-2 text-right nums">{totalBottles} btl</td><td></td><td className="py-2 text-right nums">{formatEuro(totalHt)}</td></tr></tfoot>
        </table>

        <div className="flex flex-wrap gap-4 mt-4 text-[13px]">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={opts.update_prices} onChange={(e) => setOpts((o) => ({ ...o, update_prices: e.target.checked }))} className="accent-[#b68235] w-4 h-4" data-testid="opt-update-prices" /> Mettre à jour les prix d'achat</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={opts.range_locations} onChange={(e) => setOpts((o) => ({ ...o, range_locations: e.target.checked }))} className="accent-[#b68235] w-4 h-4" data-testid="opt-range" /> Ranger aux emplacements habituels</label>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={onCancel} data-testid="invoice-cancel">Annuler</Button>
          <Button variant="primary" onClick={validate} data-testid="invoice-validate">Valider l'entrée · {totalBottles} btl</Button>
        </div>
      </div>
    </div>
  );
}
